/**
 * ArcStormSkillEffect — Lightning tree Discharge path active (fork 2 alternate).
 *
 * Dumps stored charge as 3 wild arcs targeting the nearest enemies individually.
 * Each arc deals charge-scaled damage plus a brief stagger. More selective than
 * Discharge's radial burst — picks the crowd apart rather than flattening it.
 * Consumes all static charge on activation.
 *
 * Charge scaling (per-arc):
 *   ratio = consumedCharge / maxCharge
 *   perArcDamage = baseDamageMultiplier + (ratio / maxTargets) * additionalDamageMultiplier
 *
 * Fires an onDetonation callback after arcs resolve, allowing Afterburn and Spent
 * downstream listeners to react.
 *
 * Upgrade keys (via resolver, scope: 'arc_storm'):
 * - fieldRadiusTiles: additive — Conductive Field patch radius (1 tile)
 * - fieldDurationMs: additive — Conductive Field patch duration (2000ms)
 * - fieldSlowMultiplier: additive — Conductive Field slow amount (0.3 = 30%)
 * - proximityDamagePerSecond: additive — Ambient Charge aura DPS (2/sec)
 * - proximityRadiusTiles: additive — Ambient Charge aura radius (1 tile)
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy — stagger immunity windows)
 * @see lightning-spec.md section Arc Storm (`arc_storm`)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ArcStormProjectileConfig {
  readonly maxTargets: number;
  readonly damageMultiplier: number;
  readonly sourceSkillId: string;
}

export interface ArcStormHitResult {
  readonly hits: ReadonlyArray<{ readonly hitX: number; readonly hitY: number }>;
}

export interface ArcStormZoneConfig {
  readonly type: 'ionized_patch';
  readonly positionX: number;
  readonly positionY: number;
  readonly radiusTiles: number;
  readonly durationMs: number;
  readonly speedMultiplier: number;
  readonly sourceSkillId: string;
}

/** A single enemy target returned by the melee proximity query. */
export interface ArcStormMeleeTarget {
  readonly id: string;
}

export interface ArcStormDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly projectiles: {
    fireAtNearest(config: ArcStormProjectileConfig): ArcStormHitResult;
  };
  readonly charge: {
    consume(chargeId: string): number;
  };
  readonly statusEffects: {
    apply(defId: string, opts: { sourceSkillId: string; durationMs: number }): void;
    isImmune(defId: string): boolean;
  };
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide?: () => void;
  /** Fired after arcs resolve — Afterburn and Spent listeners receive this. */
  readonly onDetonation?: () => void;
  /** Zone system — required when Conductive Field upgrade is unlocked. */
  readonly zones?: {
    create(config: ArcStormZoneConfig): void;
  };
  /**
   * Ambient Charge: returns true when any Lightning-tree skill has
   * non-zero cooldown remaining. The aura ticks only while this is true.
   */
  readonly anyLightningCooldownActive?: () => boolean;
  /**
   * Ambient Charge: proximity query returning enemies within the given
   * radius (tiles) of the player. Same pattern as Stormcaller's melee dep.
   */
  readonly melee?: {
    getTargetsInRange(radiusTiles: number): readonly ArcStormMeleeTarget[];
  };
  /**
   * Ambient Charge: per-enemy flat damage application.
   */
  readonly enemyCombat?: {
    dealDamage(enemyId: string, amount: number, sourceSkillId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ArcStormConfig {
  readonly cooldownMs: number;
  /** Maximum charge capacity — used to compute the charge ratio for scaling. */
  readonly maxCharge: number;
  /** Number of individual arc targets (3 by default). */
  readonly maxTargets: number;
  /** Stagger duration per hit in ms (200ms — spec recommendation). */
  readonly staggerMs: number;
  /** Base damage multiplier per arc at zero charge. */
  readonly baseDamageMultiplier: number;
  /** Additional damage multiplier spread across all arcs at full charge. */
  readonly additionalDamageMultiplier: number;
}

const DEFAULT_CONFIG: ArcStormConfig = {
  cooldownMs: 2000,
  maxCharge: 100,
  maxTargets: 3,
  staggerMs: 200,
  baseDamageMultiplier: 0.3,
  additionalDamageMultiplier: 1.2,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ArcStormSkillEffect implements SkillEffect {
  readonly skillId = 'arc_storm';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'MISS';
  readonly failLabel = 'FIZZLE';

  private readonly deps: ArcStormDeps;
  private readonly config: ArcStormConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _arcsHit = 0;
  private _staggerApplications = 0;
  private _totalChargeConsumed = 0;
  private _lastChargeConsumed = 0;
  private _lastChargeRatio = 0;
  private _lastPerArcDamage = 0;
  private _detonations = 0;
  private _zonesSpawned = 0;

  // Ambient Charge aura state (upgrade: arc_storm_upgrade_2).
  private _auraTick = 0;
  private _auraActive = false;
  private _auraDamageTicks = 0;
  private _auraTotalDamage = 0;

  constructor(deps: ArcStormDeps, config: Partial<ArcStormConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // -- Read-only accessors for simulation inspection --------------------------

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get arcsHit(): number { return this._arcsHit; }
  get staggerApplications(): number { return this._staggerApplications; }
  get totalChargeConsumed(): number { return this._totalChargeConsumed; }
  get lastChargeConsumed(): number { return this._lastChargeConsumed; }
  get lastChargeRatio(): number { return this._lastChargeRatio; }
  get lastPerArcDamage(): number { return this._lastPerArcDamage; }
  get detonations(): number { return this._detonations; }
  get zonesSpawned(): number { return this._zonesSpawned; }
  /** True while the Ambient Charge aura is actively dealing proximity damage. */
  get auraActive(): boolean { return this._auraActive; }
  /** Total number of 500ms aura damage ticks fired across the session. */
  get auraDamageTicks(): number { return this._auraDamageTicks; }
  /** Cumulative proximity damage dealt by the aura across all ticks. */
  get auraTotalDamage(): number { return this._auraTotalDamage; }

  // -- SkillEffect interface --------------------------------------------------

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle — skill attempt failed, no charge consumed, no cooldown
    if (!this.deps.rollSkillSuccess('arc_storm')) {
      this._fizzles++;
      return false;
    }

    this.deps.breakHide?.();

    // 1. Consume all static charge
    const consumed = this.deps.charge.consume('static_charge');
    this._lastChargeConsumed = consumed;
    this._totalChargeConsumed += consumed;

    // 2. Compute charge ratio (0 at empty, 1 at full)
    const maxCharge = this.config.maxCharge;
    const ratio = maxCharge > 0 ? Math.min(consumed / maxCharge, 1.0) : 0;
    this._lastChargeRatio = ratio;

    // 3. Compute per-arc damage multiplier (charge bonus split across maxTargets)
    const perArcDamage = this.config.baseDamageMultiplier
      + (ratio / this.config.maxTargets) * this.config.additionalDamageMultiplier;
    this._lastPerArcDamage = perArcDamage;

    // 4. Fire arcs at nearest enemies
    const result = this.deps.projectiles.fireAtNearest({
      maxTargets: this.config.maxTargets,
      damageMultiplier: perArcDamage,
      sourceSkillId: 'arc_storm',
    });

    this._activations++;

    // 5. For each hit: apply stagger (immunity-gated) and spawn zone if unlocked
    const hitCount = result.hits.length;
    this._arcsHit += hitCount;

    for (const hit of result.hits) {
      // Stagger — per-hit, gated on immunity
      if (this.config.staggerMs > 0 && !this.deps.statusEffects.isImmune('stagger')) {
        this.deps.statusEffects.apply('stagger', {
          sourceSkillId: 'arc_storm',
          durationMs: this.config.staggerMs,
        });
        this._staggerApplications++;
      }

      // Conductive Field zones (gated on upgrade being unlocked via resolver)
      this._spawnConductiveFieldZone(hit.hitX, hit.hitY);
    }

    // 6. Fire detonation event for Afterburn / Spent / downstream listeners
    this._detonations++;
    this.deps.onDetonation?.();

    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void { /* instant skill — nothing to deactivate */ }
  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Ambient Charge aura (upgrade: arc_storm_upgrade_2).
    this._tickAmbientCharge(delta);
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // -- Ambient Charge aura (upgrade: arc_storm_upgrade_2) ---------------------

  /** Tick interval for the proximity aura (500ms = 2 ticks/sec). */
  private static readonly AURA_TICK_INTERVAL_MS = 500;

  /**
   * Tick the Ambient Charge proximity aura. Active whenever the upgrade is
   * unlocked (proximityDamagePerSecond > 0) AND any Lightning-tree skill has
   * non-zero cooldown remaining. Deals flat damage every 500ms to all enemies
   * within proximityRadiusTiles of the player.
   */
  private _tickAmbientCharge(delta: number): void {
    const dps = this.deps.resolver.getAdditiveEffect('arc_storm', 'proximityDamagePerSecond');

    // Gate: upgrade not unlocked or deps not wired.
    if (dps <= 0 || !this.deps.anyLightningCooldownActive || !this.deps.melee || !this.deps.enemyCombat) {
      this._auraActive = false;
      this._auraTick = 0;
      return;
    }

    // Gate: aura only active while any Lightning skill is on cooldown.
    if (!this.deps.anyLightningCooldownActive()) {
      this._auraActive = false;
      this._auraTick = 0;
      return;
    }

    this._auraActive = true;
    this._auraTick += delta;

    const tickInterval = ArcStormSkillEffect.AURA_TICK_INTERVAL_MS;
    const damagePerTick = dps * (tickInterval / 1000);
    const radiusTiles = this.deps.resolver.getAdditiveEffect('arc_storm', 'proximityRadiusTiles');

    while (this._auraTick >= tickInterval) {
      this._auraTick -= tickInterval;

      const targets = this.deps.melee.getTargetsInRange(radiusTiles);
      for (const target of targets) {
        this.deps.enemyCombat.dealDamage(target.id, damagePerTick, 'arc_storm');
        this._auraTotalDamage += damagePerTick;
      }

      this._auraDamageTicks++;
    }
  }

  // -- Conductive Field zone spawning (upgrade: arc_storm_upgrade_1) ----------

  private _spawnConductiveFieldZone(hitX: number, hitY: number): void {
    const fieldDurationMs = this.deps.resolver.getAdditiveEffect('arc_storm', 'fieldDurationMs');
    if (fieldDurationMs <= 0) return;
    if (!this.deps.zones) return;

    const fieldRadiusTiles = this.deps.resolver.getAdditiveEffect('arc_storm', 'fieldRadiusTiles');
    const fieldSlowMultiplier = this.deps.resolver.getAdditiveEffect('arc_storm', 'fieldSlowMultiplier');

    this.deps.zones.create({
      type: 'ionized_patch',
      positionX: hitX,
      positionY: hitY,
      radiusTiles: fieldRadiusTiles,
      durationMs: fieldDurationMs,
      speedMultiplier: 1 - fieldSlowMultiplier,
      sourceSkillId: 'arc_storm',
    });

    this._zonesSpawned++;
  }
}
